import React, { useState, useEffect } from 'react';
import { FaCalendarPlus, FaUsers, FaEnvelopeOpenText, FaHistory, FaCheckCircle, FaChevronRight, FaChevronLeft, FaSearch, FaFilter, FaTimes, FaBan, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { useToast } from '../../context/ToastContext';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import EmailCampaignsNav from '../../components/ui/EmailCampaignsNav';
import DataTable from '../../components/ui/DataTable';

const CampaignScheduler = () => {
  const [step, setStep] = useState(1);
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    campaignName: '',
    eventId: '',
    templateId: '',
    targetType: 'all', // all, filtered, selected
    targetUserIds: [],
    targetFilters: {},
    scheduledAt: '',
    sendImmediately: true,
    isRecurring: false,
    recurringType: 'none',
    recurringCustomDate: ''
  });

  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const [eventsRes, templatesRes, campaignsRes, usersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users-basic-info`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setEvents(eventsRes.data.data);
      setTemplates(templatesRes.data.data);
      setCampaigns(campaignsRes.data.data);
      setUsers(usersRes.data.users || []);
    } catch (err) {
      toastError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && (!formData.campaignName || !formData.templateId)) {
      return toastError('Campaign name and template are required');
    }
    setStep(step + 1);
  };

  const handlePrev = () => setStep(step - 1);

  const handleSubmit = async () => {
    setSubmitting(true);
    const token = localStorage.getItem('adminToken');
    try {
      const payload = {
        ...formData,
        scheduledAt: formData.sendImmediately ? null : (formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : null)
      };
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Campaign scheduled successfully');
      setStep(1);
      setFormData({
        campaignName: '', eventId: '', templateId: '', targetType: 'all',
        targetUserIds: [], targetFilters: {}, scheduledAt: '', sendImmediately: true,
        isRecurring: false, recurringType: 'none', recurringCustomDate: ''
      });
      fetchInitialData();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to schedule campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setFormData(prev => ({
      ...prev,
      targetUserIds: prev.targetUserIds.includes(userId) 
        ? prev.targetUserIds.filter(id => id !== userId)
        : [...prev.targetUserIds, userId]
    }));
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  const handleCancelCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled campaign?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/${id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Campaign cancelled successfully');
      fetchInitialData();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to cancel campaign');
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign and all its logs? This action cannot be undone.')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Campaign deleted successfully');
      fetchInitialData();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to delete campaign');
    }
  };

  const renderStatusBadge = (status) => {
    const styles = {
      scheduled: 'badge-info',
      processing: 'badge-warning',
      completed: 'badge-success',
      failed: 'badge-error',
      cancelled: 'bg-gray-100 text-gray-500',
      draft: 'bg-gray-50 text-gray-400'
    };
    return <span className={`badge ${styles[status]}`}>{status}</span>;
  };

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="Email Campaigns"
        description="Create and track bulk email campaigns"
        icon={FaEnvelopeOpenText}
      />

      <EmailCampaignsNav />

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 flex items-center justify-between">
          <div className="flex gap-8">
            {[
              { id: 1, label: 'Basics', icon: <FaCalendarPlus /> },
              { id: 2, label: 'Audience', icon: <FaUsers /> },
              { id: 3, label: 'Schedule', icon: <FaEnvelopeOpenText /> },
            ].map(s => (
              <div key={s.id} className={`flex items-center gap-2 ${step === s.id ? 'text-white' : 'text-white/40'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step === s.id ? 'border-secondary-400 bg-secondary-400/20' : 'border-white/20'}`}>
                  {s.id < step ? <FaCheckCircle /> : s.icon}
                </div>
                <span className="font-bold text-sm hidden md:block">{s.label}</span>
              </div>
            ))}
          </div>
          <div className="text-white/60 text-xs font-semibold uppercase tracking-wider">Step {step} of 3</div>
        </div>

        <div className="p-8 min-h-[400px]">
          {step === 1 && (
            <div className="max-w-2xl space-y-6 animate-in slide-in-from-right duration-300">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400">Campaign Name</label>
                <input 
                  className="input-modern !bg-gray-50 dark:!bg-dark-900" 
                  placeholder="e.g., Diwali 2025 Promotional"
                  value={formData.campaignName}
                  onChange={(e) => setFormData({...formData, campaignName: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-400">Linked Event (Optional)</label>
                  <select 
                    className="input-modern !bg-gray-50 dark:!bg-dark-900"
                    value={formData.eventId}
                    onChange={(e) => setFormData({...formData, eventId: e.target.value})}
                  >
                    <option value="">None</option>
                    {events.map(e => <option key={e._id} value={e._id}>{e.eventName}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-400">Email Template</label>
                  <select 
                    className="input-modern !bg-gray-50 dark:!bg-dark-900"
                    value={formData.templateId}
                    onChange={(e) => setFormData({...formData, templateId: e.target.value})}
                  >
                    <option value="">Select Template</option>
                    {templates.map(t => <option key={t._id} value={t._id}>{t.templateName}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in slide-in-from-right duration-300">
              <div className="flex gap-4">
                {['all', 'filtered', 'selected'].map(type => (
                  <button 
                    key={type}
                    onClick={() => setFormData({...formData, targetType: type})}
                    className={`flex-1 p-4 rounded-2xl border transition-all ${formData.targetType === type ? 'bg-primary-50 border-primary-500 ring-2 ring-primary-500/20' : 'bg-transparent border-gray-100 dark:border-dark-700'}`}
                  >
                    <p className={`font-bold capitalize ${formData.targetType === type ? 'text-primary-600' : 'text-gray-400'}`}>{type}</p>
                    <p className="text-[10px] text-gray-500 mt-1 uppercase">
                      {type === 'all' ? 'All active users' : type === 'filtered' ? 'Based on services' : 'Manual pick'}
                    </p>
                  </button>
                ))}
              </div>

              {formData.targetType === 'filtered' && (
                <div className="bg-gray-50 dark:bg-dark-900 p-6 rounded-2xl border border-gray-100 dark:border-dark-700 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'isTaxFilingEnabled', label: 'Tax Filing' },
                    { key: 'isBookkeepingEnabled', label: 'Bookkeeping' },
                    { key: 'isUSAEnabled', label: 'USA Formation' },
                    { key: 'isRegisteredAgentEnabled', label: 'Registered Agent' }
                  ].map(f => (
                    <div key={f.key} className="flex items-center gap-2 font-bold text-sm">
                      <input 
                        type="checkbox" 
                        onChange={(e) => {
                          const newFilters = { ...formData.targetFilters };
                          if (e.target.checked) newFilters[f.key] = true;
                          else delete newFilters[f.key];
                          setFormData({ ...formData, targetFilters: newFilters });
                        }}
                      /> {f.label}
                    </div>
                  ))}
                </div>
              )}

              {formData.targetType === 'selected' && (
                <div className="space-y-4">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                      type="text" 
                      placeholder="Search users to add..." 
                      className="input-modern pl-10 !bg-gray-50 dark:!bg-dark-900"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto pr-2">
                    {filteredUsers.map(u => (
                      <button 
                        key={u._id}
                        onClick={() => toggleUserSelection(u._id)}
                        className={`p-3 rounded-xl border text-left text-xs transition-all ${formData.targetUserIds.includes(u._id) ? 'bg-primary-50 border-primary-500 font-bold' : 'border-gray-100 dark:border-dark-700'}`}
                      >
                        <p className={formData.targetUserIds.includes(u._id) ? 'text-primary-700' : 'dark:text-white'}>{u.name}</p>
                        <p className="text-gray-400 mt-0.5">{u.email}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="max-w-md space-y-6 animate-in slide-in-from-right duration-300">
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-900 rounded-2xl border border-gray-100 dark:border-dark-700">
                <input 
                  type="checkbox" 
                  id="immediate"
                  className="w-5 h-5"
                  checked={formData.sendImmediately}
                  onChange={(e) => setFormData({...formData, sendImmediately: e.target.checked})}
                />
                <label htmlFor="immediate" className="font-bold dark:text-white">Send Immediately</label>
              </div>

              {!formData.sendImmediately && (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase text-gray-400">Schedule Date & Time</label>
                    <input 
                      type="datetime-local" 
                      className="input-modern !bg-gray-50 dark:!bg-dark-900"
                      value={formData.scheduledAt}
                      onChange={(e) => setFormData({...formData, scheduledAt: e.target.value})}
                    />
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-900 rounded-2xl border border-gray-100 dark:border-dark-700">
                    <input 
                      type="checkbox" 
                      id="recurring"
                      className="w-5 h-5"
                      checked={formData.isRecurring}
                      onChange={(e) => setFormData({...formData, isRecurring: e.target.checked})}
                    />
                    <label htmlFor="recurring" className="font-bold dark:text-white">Make it Recurring</label>
                  </div>

                  {formData.isRecurring && (
                    <div className="space-y-4 animate-in fade-in duration-300">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-gray-400">Recurring Type</label>
                        <select 
                          className="input-modern !bg-gray-50 dark:!bg-dark-900"
                          value={formData.recurringType}
                          onChange={(e) => setFormData({...formData, recurringType: e.target.value})}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                          <option value="custom">Custom (Festival/Fixed Date)</option>
                        </select>
                      </div>

                      {formData.recurringType === 'custom' && (
                        <div className="space-y-1">
                          <label className="text-[10px] font-black uppercase text-gray-400">Fixed Date (MM-DD)</label>
                          <input 
                            type="text" 
                            placeholder="e.g., 12-25" 
                            className="input-modern !bg-gray-50 dark:!bg-dark-900"
                            value={formData.recurringCustomDate}
                            onChange={(e) => setFormData({...formData, recurringCustomDate: e.target.value})}
                          />
                          <p className="text-[10px] text-gray-500 italic">Enter month and day (e.g., 12-25 for Christmas)</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-primary-50/50 p-6 rounded-2xl border border-primary-100 space-y-3">
                <h4 className="text-[10px] font-black uppercase text-primary-600">Review Summary</h4>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Target Recipients</span>
                  <span className="font-bold text-primary-700">
                    {formData.targetType === 'all' ? 'All Users' : 
                     formData.targetType === 'filtered' ? 'Filtered Group' : 
                     `${formData.targetUserIds.length} Selected Users`}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Sending</span>
                  <span className="font-bold text-primary-700">
                    {formData.sendImmediately ? 'Now' : (formData.isRecurring ? `Recurring (${formData.recurringType})` : 'Later')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50/50 dark:bg-dark-800 border-t border-gray-100 dark:border-dark-700 flex justify-between">
          {step > 1 ? (
            <button onClick={handlePrev} className="px-6 py-2 rounded-xl font-bold flex items-center gap-2 hover:bg-gray-100 transition-colors">
              <FaChevronLeft /> Back
            </button>
          ) : <div />}
          
          {step < 3 ? (
            <button onClick={handleNext} className="btn-premium px-8 flex items-center gap-2">
              Next <FaChevronRight />
            </button>
          ) : (
            <button 
              disabled={submitting}
              onClick={handleSubmit} 
              className="btn-premium px-12 animate-float"
            >
              {submitting ? 'Scheduling...' : 'Confirm & Schedule'}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FaHistory className="text-primary-500" /> Campaign History
        </h2>
        <DataTable>
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 text-left text-sm font-semibold">Campaign Name</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Template</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Frequency</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Recipients</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Scheduled For</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Stats</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {campaigns.map(c => (
                <tr key={c._id} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{c.campaignName}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{c.template?.templateName}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${c.isRecurring ? 'badge-primary' : 'bg-gray-100 text-gray-500'}`}>
                      {c.isRecurring ? c.recurringType : 'One-time'}
                    </span>
                  </td>
                  <td className="px-4 py-3">{renderStatusBadge(c.status)}</td>
                  <td className="px-4 py-3 text-sm font-semibold">{c.stats?.total || 0} users</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {c.sendImmediately ? (
                      <span className="text-green-600 font-semibold">Immediate</span>
                    ) : (
                      c.scheduledAt ? new Date(c.scheduledAt).toLocaleString() : 'Immediate'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-3 text-xs font-semibold uppercase">
                      <span className="text-green-500">S: {c.stats?.sent}</span>
                      <span className="text-red-500">F: {c.stats?.failed}</span>
                      <span className="text-gray-400">P: {c.stats?.pending}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {['scheduled', 'draft'].includes(c.status) && (
                        <button
                          onClick={() => handleCancelCampaign(c._id)}
                          className="p-2 text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                          title="Cancel Schedule"
                        >
                          <FaBan className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteCampaign(c._id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete Campaign"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      </div>
    </PageLayout>
  );
};

export default CampaignScheduler;
